/**
 * Notification Presets
 * 
 * Standardized notification configurations to ensure consistency across the application.
 * These presets cover common notification scenarios and can be used directly with the
 * notification context methods.
 * 
 * Usage:
 * ```typescript
 * import { useNotificationContext } from '@core/ui';
 * import { notificationPresets } from '@core/ui/notifications';
 * 
 * const { notify } = useNotificationContext();
 * 
 * // Simple usage
 * notify(notificationPresets.copied('API Key'));
 * 
 * // With overrides
 * notify({ ...notificationPresets.created('Program', 'Production'), position: 'top-left' });
 * ```
 */

import { NotificationObject } from '../types';

/**
 * Notification presets for common application scenarios
 */
export const notificationPresets = {
  // ==================== Phase 1: Clipboard Operations ====================
  
  /**
   * Success notification for clipboard copy operations
   * @param itemName - Optional name of the item copied (e.g., "API Key", "Email", "URL")
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.copied()); // "Copied"
   * notify(notificationPresets.copied('Email')); // "Copied" with subtitle "Email"
   */
  copied: (itemName?: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: 'Copied',
    subtitle: itemName,
    duration: 2000,
    preventDuplicate: true,
    groupId: 'clipboard',
  }),

  /**
   * Success notification for clipboard copy with value preview
   * @param value - The value that was copied
   * @param maxLength - Maximum length to show (default: 20)
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.copiedWithPreview('abc123def456')); // "Copied: abc123def456..."
   */
  copiedWithPreview: (value: string, maxLength: number = 20): Partial<NotificationObject> => {
    const truncated = value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;
    return {
      kind: 'success',
      type: 'toast',
      title: 'Copied to clipboard',
      subtitle: truncated,
      duration: 2000,
      preventDuplicate: true,
      groupId: 'clipboard',
    };
  },

  /**
   * Error notification for failed clipboard operations
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.copyFailed());
   */
  copyFailed: (): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'callout',
    title: 'Copy failed',
    duration: 3000,
  }),

  // ==================== Phase 2: API Failures ====================

  /**
   * Generic operation failure notification
   * @param action - Optional action that failed (e.g., "create user", "update settings")
   * @param details - Optional additional details
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.operationFailed());
   * notify(notificationPresets.operationFailed('create user'));
   * notify(notificationPresets.operationFailed('save changes', 'Invalid data format'));
   */
  operationFailed: (action?: string, details?: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: action ? `Failed to ${action}` : 'Operation failed',
    subtitle: details || 'Please try again or contact support if the problem persists',
    duration: undefined, // Persist until dismissed
    priority: 5, // Higher priority for errors
  }),

  /**
   * Network error notification
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.networkError());
   */
  networkError: (): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Network error',
    subtitle: 'Check connection',
    duration: undefined,
    priority: 5,
  }),

  /**
   * Permission denied notification
   * @param action - Optional action that was denied
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.permissionDenied());
   * notify(notificationPresets.permissionDenied('delete users'));
   */
  permissionDenied: (action?: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Permission denied',
    subtitle: action 
      ? `Can't ${action}` 
      : 'Insufficient permissions',
    duration: undefined,
    priority: 5,
  }),

  /**
   * Resource not found notification
   * @param resourceType - Optional type of resource (e.g., "User", "Program", "API Key")
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.notFound());
   * notify(notificationPresets.notFound('User'));
   */
  notFound: (resourceType?: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: resourceType ? `${resourceType} not found` : 'Resource not found',
    subtitle: 'The requested resource could not be found',
    duration: 6000,
  }),

  /**
   * Validation error notification
   * @param message - Specific validation error message
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.validationError('Email address is required'));
   */
  validationError: (message: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Validation error',
    subtitle: message,
    duration: 6000,
  }),

  /**
   * Server error notification (5xx errors)
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.serverError());
   */
  serverError: (): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Server error',
    subtitle: 'An unexpected error occurred. Please try again later',
    duration: undefined,
    priority: 5,
  }),

  /**
   * Timeout error notification
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.timeout());
   */
  timeout: (): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Request timeout',
    subtitle: 'The operation took too long. Please try again',
    duration: 6000,
  }),

  // ==================== Phase 3: CRUD Operations ====================

  /**
   * Success notification for resource creation
   * @param resourceType - Type of resource created (e.g., "Program", "User", "API Key")
   * @param resourceName - Optional name of the created resource
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.created('Program')); // "Created Program"
   * notify(notificationPresets.created('API Key', 'Production')); // "Created API Key 'Production'"
   */
  created: (resourceType: string, resourceName?: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: 'Created',
    subtitle: resourceName ? `${resourceType} '${resourceName}'` : resourceType,
    duration: 2000,
  }),

  /**
   * Success notification for resource update
   * @param resourceType - Type of resource updated
   * @param resourceName - Optional name of the updated resource
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.updated('Program')); // "Updated Program"
   * notify(notificationPresets.updated('Settings', 'Email Preferences')); // "Updated Settings 'Email Preferences'"
   */
  updated: (resourceType: string, resourceName?: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: 'Updated',
    subtitle: resourceName ? `${resourceType} '${resourceName}'` : resourceType,
    duration: 2000,
  }),

  /**
   * Success notification for resource deletion
   * @param resourceType - Type of resource deleted
   * @param count - Optional count of resources deleted (for bulk operations)
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.deleted('User')); // "Deleted User"
   * notify(notificationPresets.deleted('Entitlement', 3)); // "Deleted 3 Entitlements"
   */
  deleted: (resourceType: string, count?: number): Partial<NotificationObject> => {
    if (count && count > 1) {
      const pluralType = resourceType.endsWith('s') ? resourceType : `${resourceType}s`;
      return {
        kind: 'success',
        type: 'callout',
        title: 'Deleted',
        subtitle: `${count} ${pluralType}`,
        duration: 2000,
      };
    }
    return {
      kind: 'success',
      type: 'callout',
      title: 'Deleted',
      subtitle: resourceType,
      duration: 2000,
    };
  },

  /**
   * Success notification for generic save operation
   * @param resourceType - Optional name of what was saved
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.saved()); // "Saved"
   * notify(notificationPresets.saved('Display Name')); // "Saved Display Name"
   */
  saved: (resourceType?: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: 'Saved',
    subtitle: resourceType,
    duration: 2000,
  }),

  /**
   * Success notification for resource restoration
   * @param resourceType - Type of resource restored
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.restored('User')); // "Restored User"
   */
  restored: (resourceType: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: 'Restored',
    subtitle: resourceType,
    duration: 2000,
  }),

  /**
   * Success notification for resource disabled
   * @param resourceType - Type of resource disabled
   * @param count - Optional count of resources disabled (for bulk operations)
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.disabled('Tenant')); // "Disabled Tenant"
   * notify(notificationPresets.disabled('User', 3)); // "Disabled 3 Users"
   */
  disabled: (resourceType: string, count?: number): Partial<NotificationObject> => {
    if (count && count > 1) {
      const pluralType = resourceType.endsWith('s') ? resourceType : `${resourceType}s`;
      return {
        kind: 'success',
        type: 'callout',
        title: 'Disabled',
        subtitle: `${count} ${pluralType}`,
        duration: 2000,
      };
    }
    return {
      kind: 'success',
      type: 'callout',
      title: 'Disabled',
      subtitle: resourceType,
      duration: 2000,
    };
  },

  /**
   * Success notification for resource enabled
   * @param resourceType - Type of resource enabled
   * @param count - Optional count of resources enabled (for bulk operations)
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.enabled('Tenant')); // "Enabled Tenant"
   * notify(notificationPresets.enabled('User', 3)); // "Enabled 3 Users"
   */
  enabled: (resourceType: string, count?: number): Partial<NotificationObject> => {
    if (count && count > 1) {
      const pluralType = resourceType.endsWith('s') ? resourceType : `${resourceType}s`;
      return {
        kind: 'success',
        type: 'callout',
        title: 'Enabled',
        subtitle: `${count} ${pluralType}`,
        duration: 2000,
      };
    }
    return {
      kind: 'success',
      type: 'callout',
      title: 'Enabled',
      subtitle: resourceType,
      duration: 2000,
    };
  },

  /**
   * Success notification for resource duplication
   * @param resourceType - Type of resource duplicated
   * @param resourceName - Optional name of the duplicated resource
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.duplicated('Program')); // "Duplicated Program"
   * notify(notificationPresets.duplicated('Program', 'Production')); // "Duplicated Program 'Production'"
   */
  duplicated: (resourceType: string, resourceName?: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: 'Duplicated',
    subtitle: resourceName ? `${resourceType} '${resourceName}'` : resourceType,
    duration: 2000,
  }),

  // ==================== Phase 4: Form Validation ====================

  /**
   * Error notification for missing required fields
   * @param fieldNames - Optional array of field names that are required
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.requiredFields());
   * notify(notificationPresets.requiredFields(['Email', 'Password']));
   */
  requiredFields: (fieldNames?: string[]): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Required fields missing',
    subtitle: fieldNames && fieldNames.length > 0
      ? `Please fill in: ${fieldNames.join(', ')}`
      : 'Please fill in all required fields',
    duration: 6000,
  }),

  /**
   * Error notification for invalid field format
   * @param fieldName - Name of the field with invalid format
   * @param expectedFormat - Optional description of expected format
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.invalidFormat('Email'));
   * notify(notificationPresets.invalidFormat('Phone', 'XXX-XXX-XXXX'));
   */
  invalidFormat: (fieldName: string, expectedFormat?: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: `Invalid ${fieldName.toLowerCase()} format`,
    subtitle: expectedFormat ? `Expected format: ${expectedFormat}` : undefined,
    duration: 6000,
  }),

  /**
   * Error notification for duplicate entry
   * @param fieldName - Name of the field with duplicate value
   * @param value - Optional value that is duplicated
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.duplicateEntry('Email'));
   * notify(notificationPresets.duplicateEntry('Username', 'john.doe'));
   */
  duplicateEntry: (fieldName: string, value?: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: `Duplicate ${fieldName.toLowerCase()} detected`,
    subtitle: value 
      ? `${fieldName} '${value}' is already in use`
      : `This ${fieldName.toLowerCase()} is already in use`,
    duration: 6000,
  }),

  /**
   * Error notification for value out of range
   * @param fieldName - Name of the field
   * @param min - Minimum allowed value
   * @param max - Maximum allowed value
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.valueOutOfRange('Price', 0, 1000));
   */
  valueOutOfRange: (fieldName: string, min?: number, max?: number): Partial<NotificationObject> => {
    let subtitle = '';
    if (min !== undefined && max !== undefined) {
      subtitle = `Value must be between ${min} and ${max}`;
    } else if (min !== undefined) {
      subtitle = `Value must be at least ${min}`;
    } else if (max !== undefined) {
      subtitle = `Value must be at most ${max}`;
    }
    
    return {
      kind: 'error',
      type: 'toast',
      title: `Invalid ${fieldName.toLowerCase()} value`,
      subtitle,
      duration: 6000,
    };
  },

  /**
   * Error notification for field length validation
   * @param fieldName - Name of the field
   * @param minLength - Minimum length
   * @param maxLength - Optional maximum length
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.invalidLength('Password', 8));
   * notify(notificationPresets.invalidLength('Description', 10, 500));
   */
  invalidLength: (fieldName: string, minLength?: number, maxLength?: number): Partial<NotificationObject> => {
    let subtitle = '';
    if (minLength !== undefined && maxLength !== undefined) {
      subtitle = `Must be between ${minLength} and ${maxLength} characters`;
    } else if (minLength !== undefined) {
      subtitle = `Must be at least ${minLength} characters`;
    } else if (maxLength !== undefined) {
      subtitle = `Must be at most ${maxLength} characters`;
    }
    
    return {
      kind: 'error',
      type: 'toast',
      title: `Invalid ${fieldName.toLowerCase()} length`,
      subtitle,
      duration: 6000,
    };
  },

  // ==================== Phase 6: User Actions ====================

  /**
   * Success notification for invitation sent
   * @param recipientEmail - Optional email of the recipient
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.invitationSent());
   * notify(notificationPresets.invitationSent('user@example.com'));
   */
  invitationSent: (recipientEmail?: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'toast',
    title: 'Invitation sent successfully',
    subtitle: recipientEmail ? `Sent to ${recipientEmail}` : undefined,
    duration: 4000,
  }),

  /**
   * Success notification for email sent
   * @param purpose - Purpose of the email (e.g., "verification", "password reset")
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.emailSent('verification'));
   */
  emailSent: (purpose: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'toast',
    title: `${purpose.charAt(0).toUpperCase() + purpose.slice(1)} email sent`,
    subtitle: 'Please check your inbox and spam folder',
    duration: 5000,
  }),

  /**
   * Success notification for settings saved
   * @param settingsName - Optional specific settings name
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.settingsSaved());
   * notify(notificationPresets.settingsSaved('Email Preferences'));
   */
  settingsSaved: (settingsName?: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: settingsName ? `${settingsName} saved` : 'Settings saved',
    duration: 2000,
  }),

  /**
   * Info notification for logout
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.loggedOut());
   */
  loggedOut: (): Partial<NotificationObject> => ({
    kind: 'info',
    type: 'callout',
    title: 'Logged out',
    duration: 2000,
  }),

  /**
   * Success notification for password change
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.passwordChanged());
   */
  passwordChanged: (): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'toast',
    title: 'Password changed successfully',
    subtitle: 'Please log in with your new password',
    duration: 5000,
  }),

  /**
   * Success notification for account activated
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.accountActivated());
   */
  accountActivated: (): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'toast',
    title: 'Account activated successfully',
    subtitle: 'You can now access all features',
    duration: 5000,
  }),

  /**
   * Success notification for profile updated
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.profileUpdated());
   */
  profileUpdated: (): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: 'Profile updated',
    duration: 2000,
  }),

  /**
   * Success notification for file upload
   * @param fileName - Optional name of the uploaded file
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.fileUploaded());
   * notify(notificationPresets.fileUploaded('document.pdf'));
   */
  fileUploaded: (fileName?: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: fileName ? `${fileName} uploaded` : 'File uploaded',
    duration: 2000,
  }),

  /**
   * Error notification for file upload failure
   * @param reason - Optional reason for failure
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.fileUploadFailed());
   * notify(notificationPresets.fileUploadFailed('File size exceeds limit'));
   */
  fileUploadFailed: (reason?: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'File upload failed',
    subtitle: reason || 'Please try again',
    duration: 6000,
  }),

  /**
   * Success notification for file download
   * @param itemName - Optional name of the downloaded item
   * @returns Notification configuration
   */
  downloaded: (itemName?: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: 'Downloaded',
    subtitle: itemName,
    duration: 2000,
  }),

  /**
   * Success notification for export operation
   * @param resourceType - Type of resource exported
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.exported('Users'));
   */
  exported: (resourceType: string): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'toast',
    title: `${resourceType} exported successfully`,
    subtitle: 'Download will begin shortly',
    duration: 4000,
  }),

  /**
   * Success notification for import operation
   * @param resourceType - Type of resource imported
   * @param count - Optional count of imported items
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.imported('Users'));
   * notify(notificationPresets.imported('Users', 25));
   */
  imported: (resourceType: string, count?: number): Partial<NotificationObject> => ({
    kind: 'success',
    type: 'callout',
    title: count 
      ? `${count} ${resourceType.toLowerCase()} imported`
      : `${resourceType} imported`,
    duration: 2000,
  }),

  // ==================== Warnings & Info ====================

  /**
   * Warning notification for unsaved changes
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.unsavedChanges());
   */
  unsavedChanges: (): Partial<NotificationObject> => ({
    kind: 'warning',
    type: 'toast',
    title: 'You have unsaved changes',
    subtitle: 'Please save or discard your changes before leaving',
    duration: 6000,
  }),

  /**
   * Warning notification for session expiring
   * @param minutes - Number of minutes until expiration
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.sessionExpiring(5));
   */
  sessionExpiring: (minutes: number): Partial<NotificationObject> => ({
    kind: 'warning',
    type: 'toast',
    title: 'Session expiring soon',
    subtitle: `Your session will expire in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`,
    duration: undefined,
    priority: 5,
  }),

  /**
   * Info notification for no results found
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.noResults());
   */
  noResults: (): Partial<NotificationObject> => ({
    kind: 'info',
    type: 'toast',
    title: 'No results found',
    subtitle: 'Try adjusting your search or filters',
    duration: 4000,
  }),

  /**
   * Info notification for empty state
   * @param resourceType - Type of resource
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.emptyState('Users'));
   */
  emptyState: (resourceType: string): Partial<NotificationObject> => ({
    kind: 'info',
    type: 'toast',
    title: `No ${resourceType.toLowerCase()} found`,
    subtitle: `Create your first ${resourceType.toLowerCase()} to get started`,
    duration: 5000,
  }),

  // ==================== Authentication ====================

  /**
   * Error notification for authentication failure
   * @param message - Optional custom error message
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.authenticationFailed());
   * notify(notificationPresets.authenticationFailed('Invalid credentials'));
   */
  authenticationFailed: (message?: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Authentication failed',
    subtitle: message || 'Try again',
    duration: 6000,
    priority: 5,
  }),

  /**
   * Error notification for session expired
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.sessionExpired());
   */
  sessionExpired: (): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Session expired',
    subtitle: 'Sign in again',
    duration: undefined,
    priority: 5,
  }),

  /**
   * Error notification for passkey authentication failure
   * @param message - Optional custom error message
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.passkeyFailed());
   * notify(notificationPresets.passkeyFailed('No passkey found for this device'));
   */
  passkeyFailed: (message?: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Passkey authentication failed',
    subtitle: message || 'Try different method',
    duration: 6000,
    priority: 5,
  }),

  /**
   * Error notification for rate limiting
   * @param retryAfter - Optional time to wait before retrying
   * @returns Notification configuration
   * 
   * @example
   * notify(notificationPresets.rateLimited());
   * notify(notificationPresets.rateLimited('5 minutes'));
   */
  rateLimited: (retryAfter?: string): Partial<NotificationObject> => ({
    kind: 'error',
    type: 'toast',
    title: 'Too many attempts',
    subtitle: retryAfter 
      ? `Wait ${retryAfter}`
      : 'Wait before retrying',
    duration: undefined,
    priority: 5,
  }),
};

/**
 * Type definition for notification preset functions
 */
export type NotificationPresetKey = keyof typeof notificationPresets;

/**
 * Helper to get all available preset names
 */
export const getAvailablePresets = (): NotificationPresetKey[] => {
  return Object.keys(notificationPresets) as NotificationPresetKey[];
};

